/**
 *  Declare local storage keys used within the system.
 */
export enum LocalStorageKeys {
  Token = 'token',
  Email = 'userEmail',
  AccountId = 'accountId',
}

/**
 *  A local storage wrapper to handle key prefixes.
 */
export class LocalStorageHandler {
  static get instance() {
    return LocalStorageHandler._instance;
  }
  private static _instance: LocalStorageHandler = new LocalStorageHandler();

  private clearableKeys: Set<string> = new Set<string>();

  private prefix = '';
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   *  Sets a prefix to be used for this local storage keys.
   *
   * @param prefix - The prefix string.
   */
  setPrefix(prefix: string) {
    this.prefix = prefix;
  }

  /**
   *  Sets the key value in the local storage.
   *
   * @param key - The local storage key.
   * @param value - The value to assign to the local storage key.
   * @param clearable - If the key can be removed during clearAll call.
   */
  setValue(key: string, value: string, clearable = true) {
    localStorage.setItem(this.getCompleteKey(key), value);

    if (clearable) {
      this.clearableKeys.add(key);
    }
  }

  /**
   *  Returns the key from the local storage.
   *
   * @param key - The local storage key.
   */
  getValue(key: string): string | null {
    return localStorage.getItem(this.getCompleteKey(key));
  }

  /**
   *  Removes the key from the local storage.
   *
   * @param key - The local storage key.
   */
  removeKey(key: string) {
    localStorage.removeItem(this.getCompleteKey(key));
    if (this.clearableKeys.has(key)) {
      this.clearableKeys.delete(key);
    }
  }

  /**
   *  Clears all keys that are registered by this application and is clearable.
   */
  clearAll() {
    const keys = this.clearableKeys.keys();
    for (const key of keys) {
      this.removeKey(key);
    }
  }

  /**
   *  Returns the combined prefix and key string.
   *
   * @param key - The local storage key.
   */
  private getCompleteKey(key: string) {
    return `${this.prefix ? this.prefix + '.' : ''}${key}`;
  }
}
